import {useMemo} from "react";

import {WorkspaceTour, type WorkspaceTourStep} from "./WorkspaceTour";

interface ModelingWorkbenchDetailedTourProps {
    open: boolean;
    activeTab: string;
    onClose: () => void;
    onTabChange: (tab: string) => void;
}

const TOUR_STEPS: WorkspaceTourStep[] = [
    {
        id: "workbench-header",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-header"]',
        title: "The header shows the active scenario and overall workbench context",
        body: "This is the linked modeling surface for the scenario you are editing. It also surfaces data freshness, so you can quickly see whether the underlying snapshots are current before you change the model.",
        badge: "Workbench",
        placement: "bottom",
    },
    {
        id: "workbench-scenario-assumptions",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-assumptions-register-button"]',
        title: "Scenario Assumptions is the narrative summary of this scenario",
        body: "Open this sheet to review the baseline forecast commentary and the active scenario adjustments already applied. It is a review surface for understanding the scenario, not the place to undo changes.",
        badge: "Scenario",
        placement: "bottom",
    },
    {
        id: "workbench-scenario-selector",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-scenario-selector"]',
        title: "Use the scenario selector to switch, create, or compare modeled cases",
        body: "This dropdown is where you move between scenarios before making edits. Use it to create a new case, switch back to base, or compare different versions of the model without overwriting the current scenario.",
        badge: "Scenario",
        placement: "bottom",
    },
    {
        id: "workbench-controls",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-controls"]',
        title: "These controls change the basis of the workbench run",
        body: "Baseline Method changes how trailing actuals seed the forecast. Forecast Horizon changes how many forward months the workbench projects before you review or edit any assumptions.",
        badge: "Workbench",
        placement: "bottom",
    },
    {
        id: "workbench-forecast-control-panel",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-revenue-planning"]',
        title: "The forecast control panel keeps the headline forecast read in one place",
        body: "These grouped KPIs show selected forecast-window totals for profitability, plus last-month liquidity and balance checks. When the scenario has active changes, the cards also show a vs base comparison against the baseline forecast.",
        badge: "Forecast",
        placement: "bottom",
    },
    {
        id: "workbench-view-controls",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-view-controls"]',
        title: "View controls keep the statement grids readable while you drill in",
        body: "Hide zero rows to cut noise, or show edited rows only to focus on forecast lines with active scenario changes. These filters help when the statement sections get dense.",
        badge: "Review",
        placement: "bottom",
    },
    {
        id: "workbench-statements",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-statements"]',
        title: "The statement grids are the main work surface for drilling in and editing",
        body: "The P&L, Balance Sheet, and Cash Flow stay linked here. Expand rows inline to move from statement totals into the underlying revenue, COGS, OpEx, account, customer, and supplier detail.",
        badge: "Drilldown",
        placement: "top",
    },
    {
        id: "workbench-customer-drilldown",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-statements"]',
        title: "Revenue can drill all the way down to customer level",
        body: "Use the chevrons to expand Revenue, then the revenue account, then segment or parent rows until customer rows appear. Cost rows follow the same pattern down to account and supplier rows.",
        badge: "Customers",
        placement: "top",
    },
    {
        id: "workbench-actual-transactions",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-transaction-drilldown-drawer"]',
        extraSelectors: ['[data-tour="workbench-statements"]'],
        title: "Actual cells open the transaction drawer",
        body: "Click an actual P&L cell after drilling into a source-backed row to review the transactions behind the number. Where contact mapping exists, this is the path from a statement value back to customer or supplier-level actuals.",
        badge: "Actuals",
        placement: "top",
    },
    {
        id: "workbench-forecast-overrides",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-forecast-override-popover"]',
        extraSelectors: ['[data-tour="workbench-forecast-override-trigger"]', '[data-tour="workbench-statements"]'],
        title: "Bottom-level forecast cells support Excel-like exact overrides",
        body: "Click a bottom-level forecast cell to open the compact override popover. You can type an exact value, add a note, see the formula summary, and review whether an override revision is already active.",
        badge: "Overrides",
        placement: "top",
    },
    {
        id: "workbench-forecast-assumptions-drawer",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-forecast-driver-drawer"]',
        extraSelectors: ['[data-tour="workbench-cell-detail-drawer"]', '[data-tour="workbench-statements"]'],
        title: "The full drawer shows the assumptions and driver controls",
        body: "Use Open full driver editor from the override popover, or click aggregate forecast rows, to review and edit the saved driver, template, month override, formula narrative, and scope for that line.",
        badge: "Assumptions",
        placement: "top",
    },
    {
        id: "workbench-cell-revisions",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-forecast-override-popover"]',
        extraSelectors: ['[data-tour="workbench-cell-detail-drawer"]', '[data-tour="workbench-statements"]'],
        title: "Overrides stay traceable through Ops and Revisions",
        body: "Saved exact overrides turn the cell blue and create an undoable revision. The compact popover shows cell-level Ops and Revisions when available; the full Ops and Revisions tab remains the control center for undo and review.",
        badge: "Audit",
        placement: "top",
    },
    {
        id: "workbench-scenario-assumptions",
        tab: "modeling_workbench",
        selector: '[data-tour="workbench-assumptions-drawer"]',
        extraSelectors: ['[data-tour="workbench-assumptions-register-button"]'],
        title: "Scenario Assumptions summarizes the active model logic",
        body: "Open this sheet to review the baseline commentary and active scenario adjustments. Use it as the scenario narrative and assumption checklist before or after changing forecast cells.",
        badge: "Scenario",
        placement: "top",
    },
    {
        id: "workbench-ops-tab",
        tab: "modeling_workbench",
        selector: '[data-tour="dashboard-tab-trigger-ops"]',
        title: "Ops and Revisions is where committed forecast changes can be undone",
        body: "Use this tab after the walkthrough to review active ops, filter agent versus human changes, and undo committed forecast updates. Scenario Assumptions is commentary and review only; undoing still belongs in Ops and Revisions or the cell-detail revert flow.",
        badge: "Revisions",
        placement: "top",
    },
    {
        id: "workbench-agent-selector",
        tab: "modeling_workbench",
        selector: '[data-tour="agent-panel-selector"]',
        requiresAgentPanel: true,
        title: "Use Model Expert to make changes, then switch to Model Reviewer to double-check",
        body: "Pick Model Expert when you want the agent to help change the model or guide a forecast update. After a meaningful change, switch to Model Reviewer as a second pass to sanity-check the logic and surface anything that looks off.",
        badge: "Agents",
        placement: "top",
    },
    {
        id: "workbench-agent-composer",
        tab: "modeling_workbench",
        selector: '[data-tour="agent-panel-composer"]',
        requiresAgentPanel: true,
        title: "Ask the selected agent to work in the current modeling context",
        body: "Use the chat box to tell Model Expert what to change in the scenario, then ask Model Reviewer to pressure-test the result. The panel stays context-aware to the active workspace and tab while you work.",
        badge: "Agents",
        placement: "top",
    },
];

export function ModelingWorkbenchDetailedTour({open, activeTab, onClose, onTabChange}: ModelingWorkbenchDetailedTourProps) {
    const steps = useMemo(() => TOUR_STEPS, []);

    return (
        <WorkspaceTour
            open={open}
            activeTab={activeTab}
            onClose={onClose}
            onTabChange={onTabChange}
            steps={steps}
            dialogLabel="Modeling workbench walkthrough"
            returnTabOnClose="modeling_workbench"
        />
    );
}
